import { useCallback } from 'react';
import { useWorkflowForm } from '../../../../contexts/CampaignWorkflowFormContext';
import { useSchedulingSelector } from '../../../../state/SplitTests/selectors/useSchedulingSelector';
import { useCampaignEditorContext } from 'sections/onsite-campaigns/pages/CampaignEditor/contexts/CampaignEditorContext';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { DateCantBeInThePastRule } from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Workflow/Rules/SplitTest/DateCantBeInThePastRule';

export const startDateFormKey = 'start-date';

const validate = (t, startDate: Date) => {
  if (!startDate) {
    return {
      passed: false,
      validationMessage: t(
        'onsite.editor.workflow.displayWebContentMessage.scheduling.inlineValidationMessages.startDateMissing',
      ),
    };
  }

  if (DateCantBeInThePastRule({ date: startDate }).result === 'fail') {
    return {
      passed: false,
      validationMessage: t(
        'onsite.editor.workflow.displayWebContentMessage.scheduling.inlineValidationMessages.startDateInThePast',
      ),
    };
  }

  return {
    passed: true,
    validationMessage: undefined,
  };
};

export const useStartDate = ({ splitTestId }: { splitTestId: string }) => {
  const { t } = useTranslation('campaigns');

  const { actions, state } = useWorkflowForm();
  const { dispatch } = useCampaignEditorContext();

  const onFocusChange = useCallback(
    ({ focused }) => {
      actions.setTouched(startDateFormKey, true);
      actions.setFocused(startDateFormKey, focused);
    },
    [actions],
  );

  const { startDate, endDate } = useSchedulingSelector({ splitTestId }) ?? {};
  const onDateChange = useCallback(
    (startDate: Moment) =>
      dispatch({ type: 'SET_START_DATE', payload: { id: splitTestId, startDate: startDate?.toDate() } }),
    [dispatch, splitTestId],
  );

  const validation = validate(t, startDate);

  const minDate = moment();
  const maxDate = endDate ? moment(endDate).add(-1, 'days') : undefined;

  return {
    startDate: startDate ? moment(startDate) : undefined,
    onDateChange,
    onFocusChange,
    focused: state.focused[startDateFormKey],
    validation,
    minDate,
    maxDate,
  };
};
